import {ChangeDetectorRef, Component, ComponentFactoryResolver, ViewChild, ViewEncapsulation} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {User} from '../../../model/security/user';
import {SecurityUserService} from '../../../services/security/security-user.service';
import {FormFieldConfig} from '../../../model/api/form-field-config';
import {FieldRequest} from '../../../model/api/field-request';
import {FieldRecord} from '../../../model/api/field-record';
import {CommonFieldService} from '../../../services/common/common-field.service';
import {CrudBaseComponent} from '../../../crystal-elements/crud/crud-base.component';
import {PasswordService} from "../../../services/security/password.service";
import {PasswordSecurity} from "../../../model/security/password-security";
import {UserService} from "../../../services/security/user.service";
import {WebUserSettings} from "../../../model/security/user-settings";
import TYPE = FormFieldConfig.TYPE;
import {CCRoles, CCRolesType} from "../../../model/security/cc-roles-type";
import {UserInfoStorageService} from "../../../services/auth/user-info-storage.service";
import {DateTime} from "luxon";
import {AppConfigService} from "../../../services/app-config.service";
import {DatePipe} from "@angular/common";

@Component({
  selector: 'app-security-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DatePipe,
  ],
})
export class UserComponent extends CrudBaseComponent<User> {

  // fields
  authorities: FieldRecord<any>[];
  userGroups: FieldRecord<any>[];
  notifications: FieldRecord<any>[];

  countries: FieldRecord<any>[];

  languages: FieldRecord<any>[];

  passwordSecurity: PasswordSecurity | undefined;

  batchUpdateDialogVisible: boolean = false;
  batchFormRecord: User;

  securityGrps: string[] = [];

  constructor(cdref: ChangeDetectorRef,
              commonFieldService: CommonFieldService,
              securityUserService: SecurityUserService,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService,
              private appConfigService: AppConfigService,
              private passwordService: PasswordService,
              private userService: UserService,
              private datePipe: DatePipe,
              private userInfoStorageService: UserInfoStorageService,) {
    super(cdref, messageService, confirmationService, translateService, commonFieldService);
    this.transPartKey = 'user';
    this.fieldId = 'username';
    this.crudService = securityUserService;
    this.newRecord = new User();
    this.newRecord.enabled = true;
    this.prepareForCreate = this.prepareRecordForCreate;
    this.prepareForEdit = this.prepareRecordForEdit;
    this.prepareForClone = this.prepareRecordForClone;
    this.deleteRoles = CCRoles.ROLES_SECURITY_DELETE;

    this.securityGrps = this.userInfoStorageService.getSecurityTeams();
  }

  protected initConfigs() {
    super.initConfigs();

    this.tableConfig = [
      {
        field: 'enabled',
        width: 30,
        type: 'BUTTON',
        notFilterable: true,
        notSortable: true,
        notResizable: true,
        class: value => 'centered',
        icon: value => value.enabled ? 'fas fa-toggle-on record-enabled' : 'fas fa-toggle-off record-disabled',
        command: record => {
          this.enable(record);
        },
        mapId: value => ''
      },
      {
        field: 'connected',
        notFilterable: true,
        notSortable: true,
        notResizable: true,
        mapId: value => (value.webUserSettings?.lastActivityDate
        // @ts-ignore
        && (DateTime.now().diff(DateTime.fromISO(value.webUserSettings?.lastActivityDate))).milliseconds
        < this.appConfigService.noActivityTimeout ? '<i title="Connected" class="fa-solid fa-right-to-bracket fa-fade cc-green"></i>'
          : '<i title="Disconnected" class="fa-solid fa-right-from-bracket status-disconnected"></i>')
      },
      {
        field: 'username',
      }
      ,
      {
        field: 'firstName',
      }
      ,
      {
        field: 'lastName',
      }
      ,
      {
        field: 'country',
        icon: (value: User) => value.country ? 'flag-icon flag-icon-' + value.country.toLowerCase() : '',
        mapId: value => ''
      }
      ,
      {
        field: 'language',
        icon: (value: User) => value.language ? 'flag-icon flag-icon-' + value.language.toLowerCase() : '',
        mapId: value => ''
      },
      {
        field: 'jobTitle',
      },
      {
        field: 'email',
      },
      {
        field: 'roles',
        width: 300,
        notFilterable: true,
        notSortable: true,
        showToolTip: true,
        mapId: value => value.roles && value.roles ? value.roles.join(', ') : ''
      },
      {
        field: 'userGroupNames',
        width: 300,
        notFilterable: true,
        notSortable: true,
        showToolTip: true,
        mapId: value => value.userGroupNames && value.userGroupNames ? value.userGroupNames.join(', ') : ''
      },
      {
        field: 'accountExpired',
        icon: (value: User) => 'fas fa-solid fa-' + (value.accountExpired ? 'lock' : 'lock-open'),
        mapId: value => ''
      },
      {
        field: 'accountLocked',
        icon: (value: User) => 'fas fa-solid fa-' + (value.accountLocked ? 'lock' : 'lock-open'),
        mapId: value => ''
      }, {
        field: 'forceClearCookies',
        hidden: !this.isAdmin(),
        icon: (value: User) => 'fas fa-solid fa-' + (value.webUserSettings?.forceClearCookies ? 'square-check' : 'square'),
        mapId: value => ''
      },
      {
        field: 'mailNotification',
        icon: (value: User) => 'fas fa-solid fa-' + (value.webUserSettings?.mailNotification ? 'square-check' : 'square'),
        mapId: value => ''
      },
      {
        field: 'notifActionTypesJs',
        width: 300,
        showToolTip: true,
        mapId: value => value.webUserSettings?.notifActionTypesJs ? value.webUserSettings.notifActionTypesJs : ''
      },
      {
        field: 'clearingTeams',
        width: 300,
        showToolTip: true,
        hidden: !this.isAdmin(),
        mapId: value => value.webUserSettings?.clearingTeams ? value.webUserSettings.clearingTeams : ''
      },
      {
        field: 'securityTeams',
        width: 300,
        showToolTip: true,
        hidden: !this.isAdmin(),
        mapId: value => value.webUserSettings?.securityTeams ? value.webUserSettings.securityTeams : ''
      }];
    if (this.isAdmin()) {
      this.tableConfig.push(
        {
          field: 'cookies',
          width: 200,
          showToolTip: true,
          hidden: this.isSecurityTeam(),
          mapId: value => value.webUserSettings?.cookies ? value.webUserSettings.cookies : '',
        });
    }
    this.tableConfig.push(
      {
        field: 'lastConnectionDate',
        width: 100,
        format: 'dd/MM/yyyy HH:mm:ss',
        type: 'DATE',
        notFilterable: true,
      },
      {
        field: 'changePasswordDate',
        width: 100,
        format: 'dd/MM/yyyy HH:mm:ss',
        type: 'DATE',
        notFilterable: true,
      },
      {
        field: 'lastActivityDate',
        width: 150,
        notFilterable: true,
        mapId: value => value.webUserSettings?.lastActivityDate ?
          this.datePipe.transform(value.webUserSettings?.lastActivityDate, 'dd/MM/yyyy HH:mm:ss') : '',
      },
      {
        field: 'badPasswordCount',
      }
    );

    const formAdminPart = [];

    if (this.isAdmin()) {
      formAdminPart.push(
        {
          field: 'forceClearCookies',
          column: 2,
          // disabled: value => true,
          type: TYPE.CHECKBOX,
          changed: (event, event2) => {
            if (this.formRecord.webUserSettings == null)
              this.formRecord.webUserSettings = new WebUserSettings();
            this.formRecord.webUserSettings.forceClearCookies = this.formRecord['forceClearCookies'];
          }
        },
      );
    }
    formAdminPart.push(
      {
        field: 'mailNotification',
        column: 2,
        // disabled: value => true,
        type: TYPE.CHECKBOX,
        changed: (event, event2) => {
          if (this.formRecord.webUserSettings == null)
            this.formRecord.webUserSettings = new WebUserSettings();
          this.formRecord.webUserSettings.mailNotification = this.formRecord['mailNotification'];
        },
      },
      {
        field: 'notifActionTypesJs',
        filter: true,
        column: 6,
        type: TYPE.MULTISELECT_GROUP,
        optionValue: 'id',
        options: () => this.notifications,
        changed: event => {
          if (this.formRecord.webUserSettings == null)
            this.formRecord.webUserSettings = new WebUserSettings();
          this.formRecord.webUserSettings.notifActionTypesJs = event.value;
        }
      },
    );
    if (this.isAdmin()) {
      formAdminPart.push(
        {
          field: 'clearingTeams',
          filter: true,
          column: 6,
          type: TYPE.MULTISELECT,
          options: () => this.userGroups,
          changed: event => {
            if (this.formRecord.webUserSettings == null)
              this.formRecord.webUserSettings = new WebUserSettings();
            this.formRecord.webUserSettings.clearingTeams = event.value;
          }
        },
        {
          field: 'securityTeams',
          filter: true,
          column: 6,
          type: TYPE.MULTISELECT,
          options: () => this.userGroups,
          changed: event => {
            if (this.formRecord.webUserSettings == null)
              this.formRecord.webUserSettings = new WebUserSettings();
            this.formRecord.webUserSettings.securityTeams = event.value;
          }
        });
    }

    this.formConfig = [
      {
        group: '',
        fields: [
          {
            field: 'username',
            required: true,
            column: 3,
            type: TYPE.TEXT,
          },
          {
            field: 'firstName',
            required: true,
            column: 3,
            type: TYPE.TEXT
          },
          {
            field: 'lastName',
            required: true,
            column: 3,
            type: TYPE.TEXT
          },
          {
            field: 'enabled',
            column: 3,
            type: TYPE.CHECKBOX
          },
          {
            field: 'email',
            required: false,
            column: 9,
            type: TYPE.EMAIL,
          },
          {
            type: TYPE.NONE,
            column: 3,
          },
          {
            field: 'country',
            filter: true,
            column: 3,
            type: TYPE.DROPDOWN,
            icon: record => record ? 'flag-icon flag-icon-' + record.toLowerCase() : 'fas fa-globe-europe',
            options: () => this.countries,
            changed: event => this.formRecord.country = event.value.toUpperCase()
          },
          {
            field: 'language',
            filter: true,
            column: 3,
            type: TYPE.DROPDOWN,
            icon: record => record ? 'flag-icon flag-icon-' + record.toLowerCase() : 'fas fa-globe-europe',
            options: () => this.languages,
            changed: event => this.formRecord.country = event.value.toUpperCase()
          },
          {
            field: 'jobTitle',
            column: 3,
            type: TYPE.TEXT
          },
          {
            type: TYPE.NONE,
            column: 3,
          }]
      },
      {
        group: 'password',
        fields:
          [{
            field: 'password',
            column: 4,
            type: TYPE.PASSWORD
          },
            {
              field: 'confirmPassword',
              column: 4,
              type: TYPE.PASSWORD,
              validate: record => this.validatePassword()
            },
            {
              field: 'forceChangePassword',
              column: 4,
              type: TYPE.CHECKBOX
            },
          ]
      },
      {
        group: 'roles',
        fields: [
          {
            field: 'roles',
            filter: true,
            column: 6,
            type: TYPE.MULTISELECT_GROUP,
            options: () => this.authorities,
            changed: (event, event2) => {
              if (event && event.value) {
                this.formRecord.roles = event.value;
              }
            }
          },
        ]
      },
      {
        group: 'userGroupNames',
        fields: [
          {
            field: 'userGroupNames',
            filter: true,
            column: 6,
            type: TYPE.MULTISELECT,
            options: () => this.userGroups,
          },
        ]
      },
      {
        group: 'info',
        fields: [
          {
            field: 'accountExpired',
            column: 4,
            // disabled: value => true,
            type: TYPE.CHECKBOX,
            // changed: event => {
            //   delete this.formRecord["accountNonExpired"];
            //   delete this.formRecord["credentialsNonExpired"];
            // }
          },
          {
            field: 'accountLocked',
            column: 4,
            // disabled: value => true,
            type: TYPE.CHECKBOX
          },
          // {
          //   field: 'credentialsExpired',
          //   column: 4,
          //   // disabled: value => true,
          //   type: TYPE.CHECKBOX
          // },
        ]
      },
      {
        group: 'admin',
        fields: formAdminPart
      }

    ];
    this.initButtons();
  }

  protected initButtons() {
    this.headerButtonConfig.splice(2, 0,
      {
        'tooltip': this.translateService.instant('page.' + this.transPartKey + '.table.batch_update_button'),
        'icon': 'fas fa-pen-to-square',
        class: ' p-button-help',
        command: records => this.batchUpdate(),
        enabled: () => !this.hasOneSelection() && this.hasMultipleSelection()
      },
    );
    this.headerButtonConfig = [...this.headerButtonConfig];

    this.contextMenuConfig.splice(1, 0,
      {
        id: 'batch-update',
        label: this.translateService.instant('page.' + this.transPartKey + '.table.batch_update_button'),
        icon: 'fas fa-pen-to-square',
        command: () => {
          this.batchUpdate();
        }
      }
    );
    this.contextMenuConfig = [...this.contextMenuConfig];
  }

  protected initData() {
    super.initData();

    this.settingsFormLoadingProgressValue = 0;
    //notifications
    this.notifications = (this.crudService as SecurityUserService).getNotifications();

    this.commonFieldService.getAuthorities(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.authorities = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 5;
    });

    this.commonFieldService.getUserGroups(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.userGroups = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 5;
    });

    this.commonFieldService.getCountries(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.countries = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 5;
    });
    this.commonFieldService.getLanguages(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.languages = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 5;
    });
    this.userService.getPasswordSecurity().subscribe(passwordSecurity => {
      this.passwordSecurity = passwordSecurity;
      this.settingsFormLoadingProgressValue += 100 / 5;
    });
  }

  prepareRecordForCreate(): void {
    if (this.formRecord
    ) {
      this.formRecord.webUserSettings = new WebUserSettings();
      this.formRecord['forceClearCookies'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.forceClearCookies : false;
      this.formRecord['mailNotification'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.mailNotification : false;
      this.formRecord['notifActionTypesJs'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.notifActionTypesJs : [];
      this.formRecord['clearingTeams'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.clearingTeams : [];
      this.formRecord['securityTeams'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.securityTeams : [];
      if (this.isSecurityTeam()) {
        this.formRecord.userGroupNames = this.securityGrps;
      }
    }
  }

  prepareRecordForEdit(): void {
    if (this.formRecord
    ) {
      if (!this.formRecord.webUserSettings)
        this.formRecord.webUserSettings = new WebUserSettings();
      this.formRecord['forceClearCookies'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.forceClearCookies : false;
      this.formRecord['mailNotification'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.mailNotification : false;
      this.formRecord['notifActionTypesJs'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.notifActionTypesJs : [];
      this.formRecord['clearingTeams'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.clearingTeams : [];
      this.formRecord['securityTeams'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.securityTeams : [];
      this.formRecord.originUsername = this.formRecord.username;
      this.formRecord.password = undefined;
    }
  }

  prepareRecordForClone(): void {
    if (this.formRecord
    ) {
      if (!this.formRecord.webUserSettings)
        this.formRecord.webUserSettings = new WebUserSettings();
      this.formRecord['forceClearCookies'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.forceClearCookies : false;
      this.formRecord['mailNotification'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.mailNotification : false;
      this.formRecord['notifActionTypesJs'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.notifActionTypesJs : [];
      this.formRecord['clearingTeams'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.clearingTeams : [];
      this.formRecord['securityTeams'] = this.formRecord.webUserSettings ? this.formRecord.webUserSettings.securityTeams : [];
      this.formRecord.originUsername = undefined;
      this.formRecord.username = '';
      this.formRecord.password = undefined;

      this.formRecord.accountExpired = false;
      this.formRecord.accountLocked = false;
      this.formRecord.forceChangePassword = true;
      this.formRecord.webUserSettings = {...this.formRecord.webUserSettings};
      this.formRecord.webUserSettings.username = '';
    }
  }

  enable(record: User) {
    if (record) {
      record.enabled = !record.enabled;
      this.formRecord = record;
      this.prepareRecordForEdit();
      this.save();
    }
  }

  validatePassword() {
    setTimeout(() => {
      this.globalInfoMessage = '';
      if (this.formRecord && !this.formRecord.password && !this.formRecord.confirmPassword) {
        if (!this.formRecord.originUsername)
          this.globalInfoMessage = 'page.user.form.message.user_detail_email';
        else if (this.formRecord.forceChangePassword && this.formRecord.originForceChangePassword != this.formRecord.forceChangePassword)
          this.globalInfoMessage = 'page.user.form.message.reset_password_email';
        else if (this.formRecord.originUsername != this.formRecord.username) {
          this.globalInfoMessage = 'page.user.form.message.change_username';
        }
      }
    });
    if (this.formRecord && (this.formRecord.password || this.formRecord.confirmPassword)
      && !this.passwordService.checkPasswords(this.passwordSecurity, this.formRecord.password, this.formRecord.confirmPassword)) {
      const reason = this.passwordService.getFailureReason(this.passwordSecurity, this.formRecord.password, this.formRecord.confirmPassword);
      return reason ? this.translateService.instant(reason.key, reason.params) : '';
    }
    return '';
  }

  public onSaved() {
    this.loadRecords();
  }

  private batchUpdate() {
    if (this.selectedRecords && this.selectedRecords.length > 0) {
      this.batchFormRecord = {...this.selectedRecords[0]};
      if (!this.batchFormRecord.userGroupNames)
        this.batchFormRecord.userGroupNames = [];
      if (!this.batchFormRecord.roles)
        this.batchFormRecord.roles = [];
      if (!this.batchFormRecord.webUserSettings)
        this.batchFormRecord.webUserSettings = new WebUserSettings();
      if (!this.batchFormRecord.webUserSettings)
        this.batchFormRecord.webUserSettings = new WebUserSettings();
      this.batchFormRecord['forceClearCookies'] = this.batchFormRecord.webUserSettings ? this.batchFormRecord.webUserSettings.forceClearCookies : false;
      this.batchFormRecord['mailNotification'] = this.batchFormRecord.webUserSettings ? this.batchFormRecord.webUserSettings.mailNotification : false;
      this.batchFormRecord['notifActionTypesJs'] = this.batchFormRecord.webUserSettings ? this.batchFormRecord.webUserSettings.notifActionTypesJs : [];

      this.selectedRecords.forEach(record => {
        if (record.userGroupNames) {
          record.userGroupNames.forEach(value => {
            if (!this.batchFormRecord.userGroupNames.includes(value)) {
              this.batchFormRecord.userGroupNames.push(value);
            }
          });
        }
        if (record.roles) {
          record.roles.forEach(value => {
            if (!this.batchFormRecord.roles.includes(value)) {
              this.batchFormRecord.roles.push(value);
            }
          });
        }
        if (record.webUserSettings && record.webUserSettings.notifActionTypesJs) {
          record.webUserSettings.notifActionTypesJs.forEach(value => {
            if (!this.batchFormRecord.notifActionTypesJs.includes(value)) {
              this.batchFormRecord.notifActionTypesJs.push(value);
            }
          });
        }
      });
    }

    this.batchUpdateDialogVisible = true;
  }

  refreshContextMenu() {
    this.contextMenuConfig.forEach(menu => {
      if (menu.id === 'batch-update') {
        menu.disabled = this.hasOneSelection() || !this.hasMultipleSelection()
      }
    });
    this.contextMenuConfig = [...this.contextMenuConfig];
  }

  protected isSecurityTeam(): boolean {
    return this.hasRoles([CCRolesType.Roles.ROLE_SECURITY_TEAM])&&!this.isAdmin();
  }

}
